import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Stack, Switch, Typography } from "@mui/material";
import { Refresh as RefreshIcon, SettingsSuggestOutlined } from "@mui/icons-material";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { hasPermission } from "../../config/permissionUtils";
import { useAuth } from "../../context/AuthContext";
import { inventoryService } from "../../services";

const DEFAULT_SETTINGS = {
  selective_allow_sales_during_count: false,
  selective_allow_parallel_counts: false,
  selective_enable_count_groups: false,
  selective_enable_auto_crosses: true,
  selective_allow_manual_crosses: true,
  selective_allow_zero_stock_products: false,
  general_allow_grooming_during_inventory: false,
  locked: false,
  active_selective_count: null,
  active_general_inventory: null,
};

const SELECTIVE_SWITCHES = [
  {
    field: "selective_allow_sales_during_count",
    label: "Permitir ventas durante conteo selectivo",
  },
  {
    field: "selective_allow_parallel_counts",
    label: "Permitir varios selectivos simultaneos",
    description:
      "Permite tener mas de un conteo selectivo activo al mismo tiempo. El sistema bloqueara que un mismo producto/presentacion este en dos selectivos activos.",
  },
  {
    field: "selective_enable_count_groups",
    label: "Usar grupos de conteo",
  },
  {
    field: "selective_enable_auto_crosses",
    label: "Sugerir cruces automaticos",
  },
  {
    field: "selective_allow_manual_crosses",
    label: "Permitir cruces manuales",
  },
  {
    field: "selective_allow_zero_stock_products",
    label: "Permitir productos sin existencia en selectivo",
    description: "Si esta deshabilitado, solo podras iniciar selectivos con productos cuyo stock sea mayor a 0.",
  },
];

const GENERAL_SWITCHES = [
  {
    field: "general_allow_grooming_during_inventory",
    label: "Permitir grooming durante inventario general",
    description:
      "Por defecto el inventario general congela la operacion de la sucursal. Activa esta opcion solo si grooming puede operar sin mover productos.",
  },
];

function normalizeSettings(data = {}) {
  return {
    ...DEFAULT_SETTINGS,
    ...data,
    selective_allow_sales_during_count: Boolean(data?.selective_allow_sales_during_count),
    selective_allow_parallel_counts: Boolean(data?.selective_allow_parallel_counts),
    selective_enable_count_groups: Boolean(data?.selective_enable_count_groups),
    selective_enable_auto_crosses: data?.selective_enable_auto_crosses !== false,
    selective_allow_manual_crosses: data?.selective_allow_manual_crosses !== false,
    selective_allow_zero_stock_products: Boolean(data?.selective_allow_zero_stock_products),
    general_allow_grooming_during_inventory: Boolean(data?.general_allow_grooming_during_inventory),
    locked: Boolean(data?.locked),
    active_selective_count: data?.active_selective_count || null,
    active_general_inventory: data?.active_general_inventory || null,
  };
}

function formatErrorDetail(detail) {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.join(" ");
  if (typeof detail === "object") {
    return Object.values(detail).flat().join(" ");
  }
  return String(detail);
}

function SettingsSwitchRows({ rows, settings, canManage, loading, savingField, onUpdate }) {
  return rows.map((row) => (
    <Stack
      key={row.field}
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      alignItems={{ sm: "center" }}
      justifyContent="space-between"
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {row.label}
        </Typography>
        {row.description ? (
          <Typography variant="caption" color="text.secondary">
            {row.description}
          </Typography>
        ) : null}
      </Box>
      <Switch
        checked={Boolean(settings[row.field])}
        disabled={!canManage || loading || settings.locked || savingField === row.field}
        onChange={(event) => onUpdate(row.field, event.target.checked)}
      />
    </Stack>
  ));
}

function CountSettingsPage() {
  const { currentBranch, effectivePerms = [], isSuperuser } = useAuth();
  const branchId = currentBranch?.id || null;
  const canManage = hasPermission(effectivePerms, "inventory_count_settings_manage", isSuperuser);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [savingField, setSavingField] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  const loadSettings = useCallback(async () => {
    if (!branchId) {
      setSettings(DEFAULT_SETTINGS);
      return;
    }
    setLoading(true);
    try {
      const data = await inventoryService.getCountSettings({ branch_id: branchId });
      setSettings(normalizeSettings(data));
    } catch (error) {
      setSettings(DEFAULT_SETTINGS);
      setSnack({
        open: true,
        message: error?.response?.data?.detail || "No se pudieron cargar los ajustes de conteo.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const updateField = async (field, value) => {
    if (!branchId || !canManage || settings.locked) return;
    setSettings((prev) => ({ ...prev, [field]: value }));
    setSavingField(field);
    try {
      const data = await inventoryService.updateCountSettings({
        branch_id: branchId,
        [field]: value,
      });
      setSettings(normalizeSettings(data));
      setSnack({ open: true, message: "Ajustes de conteo actualizados.", severity: "success" });
    } catch (error) {
      await loadSettings();
      setSnack({
        open: true,
        message:
          formatErrorDetail(error?.response?.data?.detail) ||
          "No puedes modificar ajustes de conteo mientras exista un conteo activo o pendiente de revision.",
        severity: "error",
      });
    } finally {
      setSavingField("");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.2}>
        <MainText
          title="Ajustes de conteo"
          subtitle="Configuracion operativa para conteos selectivos e inventario general"
          icon={SettingsSuggestOutlined}
        />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadSettings} disabled={loading}>
          Refrescar
        </Button>
      </Stack>

      {!canManage ? (
        <Alert severity="info">Solo usuarios autorizados pueden modificar estos ajustes.</Alert>
      ) : null}
      <Alert severity={settings.locked ? "warning" : "info"}>
        {settings.locked
          ? "Los ajustes estan bloqueados porque existe un conteo selectivo o inventario general activo en esta sucursal."
          : "Estos ajustes se bloquean mientras exista un conteo selectivo o inventario general activo o pendiente de revision."}
      </Alert>

      <Card variant="outlined">
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Conteo selectivo
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Define reglas usadas al crear nuevos conteos selectivos.
              </Typography>
            </Box>

            <SettingsSwitchRows
              rows={SELECTIVE_SWITCHES}
              settings={settings}
              canManage={canManage}
              loading={loading}
              savingField={savingField}
              onUpdate={updateField}
            />
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Inventario general
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Define excepciones operativas durante el inventario completo de una sucursal.
              </Typography>
            </Box>

            <SettingsSwitchRows
              rows={GENERAL_SWITCHES}
              settings={settings}
              canManage={canManage}
              loading={loading}
              savingField={savingField}
              onUpdate={updateField}
            />
          </Stack>
        </CardContent>
      </Card>

      <CustomSnackBar
        open={snack.open}
        setOpen={(open) => setSnack((prev) => ({ ...prev, open }))}
        message={snack.message}
        severity={snack.severity}
      />
    </Box>
  );
}

export default CountSettingsPage;
